import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Top-level navigation "modules" surfaced as tabs in the AppHeader.
 *
 *   `customer` — tenant-scoped workspace: active customer + LOB selectors
 *                and the menus that depend on those scopes.
 *   `global`   — system-wide settings & catalogs that aren't bound to a
 *                tenant context.
 *   `reports`  — placeholder for the reports-monitoring module; the tab
 *                is rendered disabled until the module is built.
 *
 * Routes are unchanged — switching modules only swaps which sidebar
 * sections are visible. Deep links into a page from another module
 * still work; the module state just adjusts the surrounding nav.
 */
export type NavModule = 'customer' | 'global' | 'reports';

const STORAGE_KEY = 'backoffice.navModule';

interface NavModuleContextValue {
  module: NavModule;
  setModule: (m: NavModule) => void;
}

const NavModuleContext = createContext<NavModuleContextValue | undefined>(undefined);

/** Initialise from localStorage so a reload keeps the active module. */
function readPersisted(): NavModule {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'customer' || raw === 'global' || raw === 'reports') return raw;
  } catch {
    // ignore — `localStorage` can throw in some sandboxed contexts.
  }
  return 'customer';
}

export function NavModuleProvider({ children }: { children: ReactNode }) {
  const [module, setModuleState] = useState<NavModule>(readPersisted);

  const setModule = useCallback((next: NavModule) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore — best-effort persistence.
    }
    setModuleState(next);
  }, []);

  const value = useMemo(() => ({ module, setModule }), [module, setModule]);
  return <NavModuleContext.Provider value={value}>{children}</NavModuleContext.Provider>;
}

export function useNavModule(): NavModuleContextValue {
  const ctx = useContext(NavModuleContext);
  if (!ctx) throw new Error('useNavModule must be used inside NavModuleProvider');
  return ctx;
}
